import { defineStore } from 'pinia';

import type { PartnerListSummary } from '@stuut/api-sdk/models';
import type { Partner } from '@stuut/api/models';

import { stuut } from '@/api';
import { sdk } from '@/sdk';

interface State {
  partners: Partner[];
  partnerSelect: PartnerListSummary[];
  peekLoadedPartners: Partner[];
  selectHasFullList: boolean;
  selectInitLoad: boolean;
}

let partnersPromise: Promise<Partner[]> | null = null;

export const usePartners = defineStore('partners', {
  actions: {
    async fetchPartners() {
      if (!partnersPromise) {
        partnersPromise = stuut.partners
          .list()
          .then((c) => {
            c.sort((a, b) => (a.name < b.name ? -1 : 1));
            this.partners = c;
            return this.partners;
          })
          .catch((error) => {
            console.error(error);
            return Promise.reject(error);
          });
      }
      return partnersPromise;
    },

    async fetchPartnerSelect(args?) {
      const result = await sdk.partners.list(args || {});

      this.$patch({ partnerSelect: result.data, selectHasFullList: !args?.search });

      return this.partnerSelect;
    },

    getIndexById(id) {
      return this.partners?.findIndex((record) => record.id === id);
    },

    async getPeekFindSelect(id) {
      return (
        this.getSelectById(id) ??
        this.getById(id) ??
        this.getLoadedById(id) ??
        (await this.peekFindById(id))
      );
    },

    async peekFindById(id) {
      const record = this.getById(id) || this.getLoadedById(id);

      if (record) return record;

      const partner = await stuut.partners.retrieve(id);

      this.peekLoadedPartners = [...(this.peekLoadedPartners || []), partner];

      return partner;
    },

    async setupPartnerSelect() {
      if (this.partnerSelect?.length || this.selectInitLoad) return;

      this.selectInitLoad = true;

      try {
        const partners = await this.fetchPartnerSelect();
        return partners;
      } catch (e) {
        this.selectInitLoad = false;
        throw e;
      }
    },

    syncRecord(partner: Partner) {
      if (!partner?.id) return;

      const partners = [...(this.partners || [])];
      const index = this.getIndexById(partner?.id);

      if (index === -1) {
        partners.push(partner);
      } else {
        partners[index] = partner;
      }

      this.$patch({ partners });

      return this.partners;
    },
  },
  getters: {
    getById: (state) => {
      return (partnerId: string) =>
        state.partners.find((partner) => partner.id === partnerId) as Partner | undefined;
    },

    getLoadedById: (state) => {
      return (partnerId: string) =>
        state.peekLoadedPartners.find((partner) => partner.id === partnerId) as Partner | undefined;
    },

    getSelectById: (state) => {
      return (id: string) => state.partnerSelect.find((partner) => partner?.id === id);
    },
  },
  state: (): State => ({
    partners: [],
    partnerSelect: [],
    peekLoadedPartners: [],
    selectInitLoad: false,
    selectHasFullList: false,
  }),
});

export default usePartners;
